'use client';

import { useEffect, useRef } from 'react';

type PollingOptions<T extends Array<unknown>> = {
  enabled: boolean;
  params?: T;
  onError?: (_error: Error) => void;
  maxInterval?: number;
  backoffRate?: number;
  initialInterval?: number;
  pollingTimeoutMs?: number;
};

export const usePolling = <T extends Array<unknown>, R>(
  fn: (..._args: T) => Promise<R>,
  interval: number,
  {
    enabled,
    params,
    onError,
    maxInterval = 30000,
    backoffRate = 1.05,
    initialInterval = interval,
    pollingTimeoutMs,
  }: PollingOptions<T>,
): void => {
  const timeoutRef = useRef<any | null>(null);
  const isPollingRef = useRef<boolean>(false);
  const currentIntervalRef = useRef<number>(initialInterval);
  const attemptRef = useRef<number>(0);
  const startTimeRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled) {
      isPollingRef.current = false;
      timeoutRef.current && clearTimeout(timeoutRef.current);
      startTimeRef.current = null;
      return;
    }

    const resetBackoff = (): void => {
      currentIntervalRef.current = initialInterval;
      attemptRef.current = 0;
    };

    const increaseBackoff = (): void => {
      currentIntervalRef.current = Math.min(
        currentIntervalRef.current * backoffRate,
        maxInterval,
      );
      attemptRef.current++;
    };

    const poll = async (): Promise<void> => {
      if (!isPollingRef.current) return;

      if (pollingTimeoutMs && startTimeRef.current) {
        const elapsedTime = Date.now() - startTimeRef.current;
        if (elapsedTime >= pollingTimeoutMs) {
          isPollingRef.current = false;
          timeoutRef.current && clearTimeout(timeoutRef.current);
          return;
        }
      }

      try {
        await fn(...(params ?? ([] as unknown as T)));
        resetBackoff();
      } catch (error) {
        onError?.(error as Error);
        increaseBackoff();
      }

      timeoutRef.current = setTimeout(poll, currentIntervalRef.current);
    };

    isPollingRef.current = true;
    if (!startTimeRef.current) {
      startTimeRef.current = Date.now();
    }
    timeoutRef.current = setTimeout(poll, currentIntervalRef.current);

    return () => {
      isPollingRef.current = false;
      timeoutRef.current && clearTimeout(timeoutRef.current);
    };
  }, [
    fn,
    interval,
    enabled,
    params,
    onError,
    maxInterval,
    backoffRate,
    initialInterval,
    pollingTimeoutMs,
  ]);
};
